import React, { useContext, useEffect } from 'react';
import './App.css';
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as NativeApp } from "@capacitor/app";
import Main from "./Main";
import AllErrors from './ErrorPages/AllErrors';
import NotFoundPage from './ErrorPages/NotFoundPage';
import CreateAccount from "./Login/CreateAccount";
import LoginRegister from "./Login/LoginRegister";
import Verification from "./Login/VerificationCode";
import PassengerInformation from "./PassengerInformation/PassengerInformation";
import Trip from "./TripPage/Trip";
import ChatPage from './TripPage/ChatPage';
import StopOnWay from './TripPage/StopOnWay';
import Address from "./Address/Address";
import Payment from "./Payment/Payment";
import PaymentSuccess from "./Payment/PaymentSuccess";
import PassengersTrips from './PassengerTrip/PassengersTrips';
import { StateApp } from './context/Context';
import RulesPage from './RulesPage/RulesPage';
import { requestForToken, subscribeToForegroundMessages } from "./firebase";
import SupportPage from './SupportPage/SupportPage';
import UserTickets from './SupportPage/UserTickets';
import GetProfile from './profile/GetProfile';
import { LuMenu } from 'react-icons/lu';
import Navbar from './header/Navbar';
import { AnimatePresence, motion } from 'framer-motion';
import SingleTripDetails from './PassengerTrip/SingleTripDetails';
import Wallet from './Payment/Wallet';
import Transactions from './Transactions/Transactions';
import './styles/marankoo-redesign.css';
import { registerMarankooServiceWorker } from './pwa/serviceWorker';

function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const { ShowNavbar, setShowNavbar } = useContext(StateApp);

    useEffect(() => {
        if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
            return undefined;
        }

        let active = true;
        let listener;
        const handlePaymentReturn = (incomingUrl) => {
            if (!active || typeof incomingUrl !== 'string') return;
            let incoming;
            try {
                incoming = new URL(incomingUrl);
            } catch (_) {
                return;
            }
            const verifiedWebLink =
                incoming.origin === 'https://passenger.marankoo.ir' &&
                incoming.pathname === '/payment-return.html';
            const fallbackAppLink =
                incoming.protocol === 'marankoo-passenger:' &&
                incoming.hostname === 'payment-return' &&
                (incoming.pathname === '' || incoming.pathname === '/');
            if (!verifiedWebLink && !fallbackAppLink) return;

            const result = incoming.searchParams.get('result');
            if (result !== 'success' && result !== 'failure') return;

            // The URL is only a navigation hint. Actual payment status is read
            // from the existing authenticated trip endpoints after navigation.
            if (result === 'success') {
                navigate('/passengersTrips', { replace: true });
            } else {
                const canRetry = Boolean(
                    localStorage.getItem('travelID') && localStorage.getItem('travelData')
                );
                navigate(canRetry ? '/Payment' : '/passengersTrips', { replace: true });
            }
        };

        NativeApp.addListener('appUrlOpen', ({ url }) => {
            handlePaymentReturn(url);
        }).then((registered) => {
            if (active) listener = registered;
            else registered.remove();
        }).catch((error) => console.error('Payment return listener failed:', error));

        NativeApp.getLaunchUrl().then((launch) => {
            if (launch && launch.url) handlePaymentReturn(launch.url);
        }).catch((error) => console.error('Payment launch URL failed:', error));

        return () => {
            active = false;
            if (listener) listener.remove();
        };
    }, [navigate]);

    const handleNavbar = () => setShowNavbar((previous) => !previous);

    useEffect(() => {
        // Close the drawer when navigation occurs (including browser back).
        setShowNavbar(false);
    }, [location.pathname, setShowNavbar]);

    useEffect(() => {
        if (!ShowNavbar) return undefined;
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') setShowNavbar(false);
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [ShowNavbar, setShowNavbar]);

    useEffect(() => {
        registerMarankooServiceWorker();
    }, []);

    useEffect(() => {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) return undefined;

        // Safe on bootstrap: this only syncs FCM when permission is already granted.
        // It never opens a browser permission prompt by itself.
        requestForToken(accessToken);

        let unsubscribe = () => {};
        let active = true;

        subscribeToForegroundMessages(() => {
            // Individual pages can show their own in-app notification.
        }).then((listener) => {
            if (!active) {
                if (typeof listener === "function") listener();
                return;
            }
            unsubscribe = listener;
        });

        return () => {
            active = false;
            if (typeof unsubscribe === "function") unsubscribe();
        };
    }, []);

    return (
        <div className='relative mk-app-shell'>
            <button
                type="button"
                className="mk-mobile-menu-trigger"
                onClick={handleNavbar}
                aria-label={ShowNavbar ? 'بستن منو' : 'باز کردن منو'}
                aria-expanded={ShowNavbar}
                aria-controls="mk-passenger-menu"
            >
                <LuMenu aria-hidden="true" size={25} />
            </button>

            <AnimatePresence>
                {ShowNavbar && (
                        <motion.div
                            key="passenger-backdrop"
                            className="mk-menu-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowNavbar(false)}
                            aria-hidden="true"
                        />
                )}
                {ShowNavbar && (
                        <motion.aside
                            key="passenger-drawer"
                            id="mk-passenger-menu"
                            className="mk-menu-panel"
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'tween', duration: 0.22 }}
                            role="dialog"
                            aria-modal="true"
                            aria-label="منوی مسافر"
                        >
                            <Navbar />
                        </motion.aside>
                )}
            </AnimatePresence>

            {/* Single source of truth for passenger profile bootstrap. */}
            <GetProfile />

            <Routes>
                <Route path="/" element={<Main />} />
                <Route path="/LoginRegister" element={<LoginRegister />} />
                <Route path="/notFound" element={<NotFoundPage />} />
                <Route path="/errorPage" element={<AllErrors />} />
                <Route path="/CreateAccount" element={<CreateAccount />} />
                <Route path="/Verification" element={<Verification />} />
                <Route path="/messagePage" element={<ChatPage />} />
                <Route path="/optionPage" element={<StopOnWay />} />
                <Route path="/passenger/:travelID" element={<PassengerInformation />} />
                <Route path="/trip/:id" element={<Trip />} />
                <Route path="/passengersTrips" element={<PassengersTrips />} />
                <Route path="/passengersTrips/:id" element={<SingleTripDetails />} />
                <Route path="/address/:travelID" element={<Address />} />
                <Route path="/Payment" element={<Payment />} />
                <Route path="/successPayment" element={<PaymentSuccess />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/userTickets" element={<UserTickets />} />
                <Route path="/rules" element={<RulesPage />} />
                <Route path="/walletValue" element={<Wallet />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </div>
    );
}

export default App;
